<template>
  <div class="drama-episode-management">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>短剧集数管理</span>
        <div style="float: right;">
          <el-button type="primary" size="small" icon="el-icon-upload2" @click="handleBatchUploadOpen">
            批量上传
          </el-button>
          <el-button type="text" style="padding: 3px 0; margin-left: 8px;" @click="handleAdd">
            <i class="el-icon-plus"></i> 新增集数
          </el-button>
        </div>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar" style="margin-bottom: 20px;">
        <el-select
          v-model="searchDramaId"
          placeholder="选择短剧（留空显示全部）"
          clearable
          filterable
          style="width: 250px; margin-right: 10px;"
          @change="handleSearch"
        >
          <el-option
            v-for="item in dramaList"
            :key="item.id"
            :label="item.title || item.name"
            :value="item.id"
          >
          </el-option>
        </el-select>
        <el-input
          v-model="searchKeyword"
          placeholder="请输入集数标题搜索"
          prefix-icon="el-icon-search"
          clearable
          style="width: 300px;"
          @clear="handleSearch"
          @keyup.enter.native="handleSearch"
        >
        </el-input>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch" style="margin-left: 10px;">搜索</el-button>
        <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        <el-tag v-if="searchDramaId" type="success" style="margin-left: 10px;">已筛选</el-tag>
        <el-tag v-else type="info" style="margin-left: 10px;">显示全部</el-tag>
      </div>

      <!-- 数据表格 -->
      <el-table
        :data="episodeList"
        border
        stripe
        v-loading="loading"
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" align="center"></el-table-column>
        <el-table-column prop="dramaName" label="所属短剧" min-width="150">
          <template slot-scope="scope">
            {{ scope.row.dramaName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="episodeNumber" label="集数" width="100" align="center"></el-table-column>
        <el-table-column prop="title" label="集数标题" min-width="200"></el-table-column>
        <el-table-column prop="duration" label="时长(秒)" width="120" align="center"></el-table-column>
        <el-table-column prop="videoUrl" label="视频地址" min-width="250" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.videoUrl">{{ scope.row.videoUrl }}</span>
            <span v-else style="color: #909399;">未上传</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180" align="center">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createdAt || scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" icon="el-icon-edit" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="danger" icon="el-icon-delete" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        style="margin-top: 20px; text-align: right;"
      >
      </el-pagination>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      @close="handleDialogClose"
    >
      <el-form ref="episodeForm" :model="episodeForm" :rules="episodeRules" label-width="100px">
        <el-form-item label="所属短剧" prop="dramaId">
          <el-select
            v-model="episodeForm.dramaId"
            placeholder="请选择短剧"
            filterable
            style="width: 100%;"
          >
            <el-option
              v-for="item in dramaList"
              :key="item.id"
              :label="item.title || item.name"
              :value="item.id"
            >
            </el-option>
          </el-select>
          <div class="form-tip" style="color: #909399; font-size: 12px; margin-top: 5px;">
            ℹ️ 显示短剧名称而非ID
          </div>
        </el-form-item>
        <el-form-item label="集数" prop="episodeNumber">
          <el-input-number v-model="episodeForm.episodeNumber" :min="1" :max="9999" style="width: 100%;"></el-input-number>
        </el-form-item>
        <el-form-item label="集数标题" prop="title">
          <el-input v-model="episodeForm.title" placeholder="请输入集数标题"></el-input>
        </el-form-item>
        <el-form-item label="时长(秒)" prop="duration">
          <el-input-number 
            v-model="episodeForm.duration" 
            :min="1" 
            :max="99999" 
            style="width: 100%;"
            disabled
          ></el-input-number>
          <div class="form-tip" style="color: #67C23A; font-size: 12px; margin-top: 5px;">
            ⏱️ 上传视频后自动获取时长
          </div>
        </el-form-item>
        <el-form-item label="视频来源" prop="videoSourceType">
          <el-radio-group v-model="episodeForm.videoSourceType" @change="handleVideoSourceChange">
            <el-radio label="upload">上传文件</el-radio>
            <el-radio label="url">输入URL</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <!-- 上传文件方式 -->
        <el-form-item v-if="episodeForm.videoSourceType === 'upload'" label="视频文件" prop="videoFile">
          <el-upload
            class="video-uploader"
            :action="uploadVideoUrl"
            :headers="uploadHeaders"
            :on-success="handleVideoSuccess"
            :on-error="handleUploadError"
            :before-upload="beforeVideoUpload"
            :show-file-list="false"
            accept="video/*"
          >
            <div v-if="episodeForm.videoUrl" class="video-preview">
              <i class="el-icon-video-camera" style="font-size: 48px; color: #409EFF;"></i>
              <div style="margin-top: 10px; color: #67C23A;">
                <i class="el-icon-success"></i> 视频已上传
              </div>
              <div style="margin-top: 5px; font-size: 12px; color: #909399; word-break: break-all;">
                {{ episodeForm.videoUrl }}
              </div>
              <div style="margin-top: 10px;">
                <el-button size="mini" type="danger" @click.stop="handleRemoveVideo">
                  <i class="el-icon-delete"></i> 移除视频
                </el-button>
              </div>
            </div>
            <div v-else class="video-uploader-icon">
              <i class="el-icon-upload" style="font-size: 48px;"></i>
              <div style="margin-top: 10px;">点击上传视频</div>
            </div>
          </el-upload>
          <div class="upload-tip" style="color: #909399; font-size: 12px; margin-top: 5px;">
            📹 支持格式: mp4, avi, mov, wmv, flv, mkv | 文件大小 ≤ 100MB
          </div>
        </el-form-item>
        
        <!-- URL输入方式 -->
        <el-form-item v-else label="视频URL" prop="videoUrlInput">
          <el-input 
            v-model="episodeForm.videoUrl" 
            placeholder="请输入视频URL地址"
            clearable
          ></el-input>
          <div class="form-tip" style="color: #909399; font-size: 12px; margin-top: 5px;">
            💡 请输入完整的视频链接地址（如：https://example.com/video.mp4）
          </div>
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="episodeForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 批量上传对话框 -->
    <el-dialog
      title="批量上传短剧视频"
      :visible.sync="batchDialogVisible"
      width="800px"
      @close="handleBatchDialogClose"
    >
      <el-form label-width="100px">
        <el-form-item label="所属短剧" required>
          <el-select
            v-model="batchForm.dramaId"
            placeholder="请选择短剧"
            filterable
            style="width: 100%;"
          >
            <el-option
              v-for="item in dramaList"
              :key="item.id"
              :label="item.title || item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="智能排序">
          <el-switch
            v-model="batchForm.autoOrder"
            active-text="从文件名提取集数（第01集、EP01、01.mp4）"
            inactive-text="手动指定集数"
          />
        </el-form-item>
        <el-form-item label="视频文件" required>
          <el-upload
            ref="batchUpload"
            action="#"
            :auto-upload="false"
            :on-change="handleBatchFileChange"
            :on-remove="handleBatchFileRemove"
            :before-upload="beforeBatchVideoUpload"
            multiple
            accept="video/*"
            drag
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将视频拖到此处，或<em>点击选择</em></div>
            <div slot="tip" class="el-upload__tip">
              支持 mp4、avi、mov、wmv、flv、mkv，单文件 ≤ 100MB，可多选
            </div>
          </el-upload>
        </el-form-item>
      </el-form>

      <el-table
        v-if="batchPreviewList.length"
        :data="batchPreviewList"
        border
        size="small"
        style="width: 100%; margin-top: 10px;"
        max-height="300"
      >
        <el-table-column prop="originalFilename" label="文件名" min-width="160" show-overflow-tooltip />
        <el-table-column label="集数" width="120" align="center">
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.episodeNumber"
              :min="1"
              :max="9999"
              size="mini"
              controls-position="right"
            />
          </template>
        </el-table-column>
        <el-table-column label="标题" min-width="140">
          <template slot-scope="scope">
            <el-input v-model="scope.row.title" size="mini" placeholder="集数标题" />
          </template>
        </el-table-column>
        <el-table-column prop="videoUrl" label="视频地址" min-width="180" show-overflow-tooltip />
        <el-table-column prop="duration" label="时长(秒)" width="90" align="center" />
      </el-table>

      <div slot="footer" class="dialog-footer">
        <el-button @click="batchDialogVisible = false">取 消</el-button>
        <el-button
          type="primary"
          :loading="batchUploadLoading"
          :disabled="!batchFileList.length"
          @click="handleBatchUploadVideos"
        >
          {{ batchPreviewList.length ? '重新上传' : '上传视频' }}
        </el-button>
        <el-button
          type="success"
          :loading="batchImportLoading"
          :disabled="!batchPreviewList.length"
          @click="handleBatchImport"
        >
          确认导入
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { request, BASE_URL } from '@/utils/http.js'

export default {
  name: 'DramaEpisodeManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      batchDialogVisible: false,
      batchUploadLoading: false,
      batchImportLoading: false,
      batchFileList: [],
      batchPreviewList: [],
      batchForm: {
        dramaId: null,
        autoOrder: true
      },
      searchDramaId: null,
      searchKeyword: '',
      episodeList: [],
      dramaList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '新增集数',
      episodeForm: {
        id: null,
        dramaId: null,
        episodeNumber: 1,
        title: '',
        duration: 0,
        videoUrl: '',
        videoFile: null,  // 新增:用于存储上传的文件
        videoSourceType: 'upload',  // 视频来源类型：'upload' 或 'url'
        status: 1
      },
      episodeRules: {
        dramaId: [
          { required: true, message: '请选择所属短剧', trigger: 'change' }
        ],
        episodeNumber: [
          { required: true, message: '请输入集数', trigger: 'blur' }
        ],
        title: [
          { required: true, message: '请输入集数标题', trigger: 'blur' },
          { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        videoFile: [
          { 
            validator: (rule, value, callback) => {
              if (this.episodeForm.videoSourceType === 'upload') {
                if (!this.episodeForm.videoUrl) {
                  callback(new Error('请上传视频文件'))
                } else {
                  callback()
                }
              } else {
                callback()
              }
            }, 
            trigger: 'change' 
          }
        ],
        videoUrlInput: [
          { 
            validator: (rule, value, callback) => {
              if (this.episodeForm.videoSourceType === 'url') {
                if (!this.episodeForm.videoUrl || !this.episodeForm.videoUrl.trim()) {
                  callback(new Error('请输入视频URL地址'))
                } else {
                  callback()
                }
              } else {
                callback()
              }
            }, 
            trigger: 'blur' 
          }
        ]
      }
    }
  },
  computed: {
    // 上传视频接口URL
    uploadVideoUrl() {
      return BASE_URL + '/admin/drama/upload/video'
    },
    // 上传请求头
    uploadHeaders() {
      return {
        'Authorization': localStorage.getItem('token') || ''
      }
    }
  },
  mounted() {
    this.loadDramaList()
    // 如果有路由参数中的dramaId,则自动选择该短剧
    if (this.$route.query.dramaId) {
      this.searchDramaId = parseInt(this.$route.query.dramaId)
    }
    this.loadEpisodeList()
  },
  methods: {
    getResponseMessage(response) {
      return response.msg || response.message || ''
    },

    // 打开批量上传对话框
    handleBatchUploadOpen() {
      this.batchForm = {
        dramaId: this.searchDramaId || null,
        autoOrder: true
      }
      this.batchFileList = []
      this.batchPreviewList = []
      this.batchDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.batchUpload) {
          this.$refs.batchUpload.clearFiles()
        }
      })
    },

    handleBatchDialogClose() {
      this.batchFileList = []
      this.batchPreviewList = []
      if (this.$refs.batchUpload) {
        this.$refs.batchUpload.clearFiles()
      }
    },

    beforeBatchVideoUpload(file) {
      const ext = file.name.split('.').pop().toLowerCase()
      const validExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv']
      const isLt100M = file.size / 1024 / 1024 < 100
      if (!validExts.includes(ext)) {
        this.$message.error(`文件 ${file.name} 格式不支持`)
        return false
      }
      if (!isLt100M) {
        this.$message.error(`文件 ${file.name} 超过 100MB`)
        return false
      }
      return true
    },

    handleBatchFileChange(file, fileList) {
      this.batchFileList = fileList.map(item => item.raw).filter(Boolean)
    },

    handleBatchFileRemove(file, fileList) {
      this.batchFileList = fileList.map(item => item.raw).filter(Boolean)
      if (!this.batchFileList.length) {
        this.batchPreviewList = []
      }
    },

    filenameToTitle(filename) {
      if (!filename) return ''
      const dotIndex = filename.lastIndexOf('.')
      return dotIndex > 0 ? filename.substring(0, dotIndex) : filename
    },

    async uploadVideosBatch(files, dramaId, autoOrder) {
      const formData = new FormData()
      files.forEach(file => formData.append('files', file))
      if (dramaId) {
        formData.append('dramaId', dramaId)
      }
      formData.append('autoOrder', autoOrder)

      const token = localStorage.getItem('token')
      const headers = {}
      if (token && token !== 'null' && token !== 'undefined') {
        headers.Authorization = token
      }

      const response = await fetch(`${BASE_URL}/admin/drama/upload/videos`, {
        method: 'POST',
        headers,
        body: formData
      })

      if (response.status === 401) {
        throw new Error('登录已过期，请重新登录')
      }

      const result = await response.json()
      if (result.code === 200) {
        return result
      }
      throw new Error(this.getResponseMessage(result) || '批量视频上传失败')
    },

    getVideoDurationAsync(videoUrl) {
      return new Promise(resolve => {
        const video = document.createElement('video')
        video.preload = 'metadata'
        const timer = setTimeout(() => {
          window.URL.revokeObjectURL(video.src)
          resolve(0)
        }, 8000)
        video.onloadedmetadata = () => {
          clearTimeout(timer)
          window.URL.revokeObjectURL(video.src)
          resolve(Math.ceil(video.duration) || 0)
        }
        video.onerror = () => {
          clearTimeout(timer)
          resolve(0)
        }
        video.src = videoUrl
      })
    },

    async buildBatchPreviewList(episodes) {
      const list = []
      for (let i = 0; i < episodes.length; i++) {
        const item = episodes[i]
        const episodeNumber = item.episodeNumber != null
          ? item.episodeNumber
          : (this.batchForm.autoOrder ? null : i + 1)
        const duration = await this.getVideoDurationAsync(item.videoUrl)
        list.push({
          videoUrl: item.videoUrl,
          originalFilename: item.originalFilename || '',
          episodeNumber,
          title: this.filenameToTitle(item.originalFilename),
          duration,
          status: 1
        })
      }
      return list
    },

    async handleBatchUploadVideos() {
      if (!this.batchForm.dramaId) {
        this.$message.warning('请先选择所属短剧')
        return
      }
      if (!this.batchFileList.length) {
        this.$message.warning('请选择要上传的视频文件')
        return
      }

      this.batchUploadLoading = true
      try {
        const response = await this.uploadVideosBatch(
          this.batchFileList,
          this.batchForm.dramaId,
          this.batchForm.autoOrder
        )
        const episodes = (response.data && response.data.episodes) || []
        if (!episodes.length) {
          this.$message.warning('未获取到上传结果')
          return
        }
        this.batchPreviewList = await this.buildBatchPreviewList(episodes)
        this.$message.success(this.getResponseMessage(response) || `成功上传 ${episodes.length} 个视频`)
      } catch (error) {
        this.$message.error(error.message || '批量视频上传失败')
      } finally {
        this.batchUploadLoading = false
      }
    },

    async handleBatchImport() {
      if (!this.batchForm.dramaId) {
        this.$message.warning('请先选择所属短剧')
        return
      }
      if (!this.batchPreviewList.length) {
        this.$message.warning('请先上传视频')
        return
      }

      const invalidEpisode = this.batchPreviewList.find(
        item => !item.episodeNumber || !item.title || !item.videoUrl
      )
      if (invalidEpisode) {
        this.$message.warning('请为每条记录填写集数、标题和视频地址')
        return
      }

      const episodeNumbers = this.batchPreviewList.map(item => item.episodeNumber)
      const uniqueNumbers = new Set(episodeNumbers)
      if (uniqueNumbers.size !== episodeNumbers.length) {
        this.$message.warning('集数序号不能重复')
        return
      }

      this.batchImportLoading = true
      try {
        const submitData = this.batchPreviewList.map(item => ({
          dramaId: this.batchForm.dramaId,
          episodeNumber: item.episodeNumber,
          title: item.title.trim(),
          duration: item.duration || 0,
          videoUrl: item.videoUrl,
          status: item.status
        }))

        const response = await request('/admin/drama/episodes/batch-add', {
          method: 'POST',
          body: JSON.stringify(submitData)
        })

        if (response.code === 200) {
          this.$message.success(`成功导入 ${submitData.length} 集`)
          this.batchDialogVisible = false
          this.searchDramaId = this.batchForm.dramaId
          this.loadEpisodeList()
        } else {
          this.$message.error(this.getResponseMessage(response) || '导入失败')
        }
      } catch (error) {
        this.$message.error('导入失败: ' + (error.message || '未知错误'))
      } finally {
        this.batchImportLoading = false
      }
    },

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      return dateTime.replace('T', ' ')
    },
    
    // 加载短剧列表
    async loadDramaList() {
      try {
        const response = await request('/admin/drama/list', { 
          method: 'GET',
          params: { pageNum: 1, pageSize: 1000 }
        })
        
        if (response.code === 200 && response.data) {
          this.dramaList = response.data.records || []
          console.log('短剧列表加载成功:', this.dramaList.length, '个短剧')
          console.log('短剧列表示例:', this.dramaList.slice(0, 3))
        } else {
          console.error('短剧列表加载失败:', response.msg)
        }
      } catch (error) {
        console.error('加载短剧列表失败:', error)
        this.$message.error('加载短剧列表失败')
      }
    },

    // 加载集数列表
    async loadEpisodeList() {
      this.loading = true
      try {
        // 使用新的list接口获取所有集数
        const response = await request('/admin/drama/episodes/list', { 
          method: 'GET'
        })
        
        if (response.code === 200 && response.data) {
          console.log('集数列表加载成功:', response.data.length, '条记录')
          
          // 后端已返回dramaName，直接使用
          this.episodeList = response.data || []
          
          // 如果有选择短剧,则过滤显示该短剧的集数
          if (this.searchDramaId) {
            this.episodeList = this.episodeList.filter(ep => ep.dramaId === this.searchDramaId)
            console.log('已筛选短剧ID:', this.searchDramaId, '剩余集数:', this.episodeList.length)
          } else {
            // searchDramaId 为 null，显示全部集数
            console.log('显示全部集数（未筛选）')
          }
          
          // 如果有搜索关键词,则过滤集数
          if (this.searchKeyword) {
            const keyword = this.searchKeyword.toLowerCase()
            this.episodeList = this.episodeList.filter(ep => 
              ep.title.toLowerCase().includes(keyword)
            )
            console.log('按关键词筛选后剩余:', this.episodeList.length)
          }
          
          this.pagination.total = this.episodeList.length
        } else {
          this.$message.error(response.msg || '加载失败')
        }
      } catch (error) {
        this.$message.error('加载集数列表失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.page = 1
      this.loadEpisodeList()
    },

    // 重置
    handleReset() {
      this.searchDramaId = null
      this.searchKeyword = ''
      this.pagination.page = 1
      this.loadEpisodeList()
    },

    // 新增
    handleAdd() {
      this.dialogTitle = '新增集数'
      this.episodeForm = {
        id: null,
        dramaId: null,
        episodeNumber: 1,
        title: '',
        duration: 0,
        videoUrl: '',
        videoFile: null,
        videoSourceType: 'upload',  // 默认选择上传文件方式
        status: 1
      }
      this.dialogVisible = true
    },

    // 编辑
    handleEdit(row) {
      this.dialogTitle = '编辑集数'
      // 判断视频来源：如果有URL且不是上传的格式，则认为是URL输入方式
      const isUrlInput = row.videoUrl && !row.videoUrl.includes('/admin/drama/upload/')
      
      this.episodeForm = {
        id: row.id,
        dramaId: row.dramaId,
        episodeNumber: row.episodeNumber,
        title: row.title,
        duration: row.duration,
        videoUrl: row.videoUrl,
        videoFile: null,  // 编辑时不加载文件对象
        videoSourceType: isUrlInput ? 'url' : 'upload',
        status: row.status
      }
      this.dialogVisible = true
    },

    // 视频来源切换
    handleVideoSourceChange(sourceType) {
      // 切换时清空视频URL和文件
      this.episodeForm.videoUrl = ''
      this.episodeForm.videoFile = null
      this.episodeForm.duration = 0
      
      if (sourceType === 'upload') {
        this.$message.info('已切换到上传文件方式')
      } else {
        this.$message.info('已切换到输入URL方式')
      }
    },

    // 视频上传前验证
    beforeVideoUpload(file) {
      const validTypes = ['video/mp4', 'video/avi', 'video/quicktime', 'video/x-ms-wmv', 'video/x-flv', 'video/x-matroska']
      const isValidType = validTypes.some(type => file.type === type || file.name.toLowerCase().endsWith(type.split('/')[1]))
      const isLt100M = file.size / 1024 / 1024 < 100

      if (!isValidType) {
        this.$message.error('只能上传 mp4, avi, mov, wmv, flv, mkv 格式的视频文件!')
        return false
      }
      if (!isLt100M) {
        this.$message.error('视频文件大小不能超过 100MB!')
        return false
      }
      
      // 存储文件对象用于验证
      this.episodeForm.videoFile = file
      return true
    },

    // 视频上传成功
    handleVideoSuccess(response) {
      if (response.code === 200) {
        this.episodeForm.videoUrl = response.data
        this.$message.success('视频上传成功')
        
        // 自动获取视频时长
        this.getVideoDuration(response.data)
      } else {
        this.$message.error(response.msg || '视频上传失败')
        // 上传失败,清空videoUrl
        this.episodeForm.videoUrl = ''
      }
    },

    // 获取视频时长
    getVideoDuration(videoUrl) {
      const video = document.createElement('video')
      video.preload = 'metadata'
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src)
        const duration = Math.ceil(video.duration) // 向上取整为秒
        this.episodeForm.duration = duration
        this.$message.success(`已自动获取视频时长: ${duration}秒`)
        console.log('视频时长:', duration, '秒')
      }
      
      video.onerror = () => {
        console.error('无法获取视频时长')
        this.$message.warning('无法自动获取视频时长,请手动输入')
      }
      
      video.src = videoUrl
    },

    // 上传失败
    handleUploadError() {
      this.$message.error('视频上传失败,请重试')
      this.episodeForm.videoUrl = ''
    },

    // 移除已上传的视频
    handleRemoveVideo() {
      this.$confirm('确定要移除已上传的视频吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.episodeForm.videoUrl = ''
        this.episodeForm.videoFile = null
        this.episodeForm.duration = 0  // 清空时长
        this.$message.success('已移除视频')
        
        // 如果是URL输入方式，切换回上传文件方式
        if (this.episodeForm.videoSourceType === 'url') {
          this.episodeForm.videoSourceType = 'upload'
        }
      }).catch(() => {})
    },

    // 删除
    handleDelete(row) {
      this.$confirm(`确定要删除第${row.episodeNumber}集吗?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await request(`/admin/drama/episode/delete/${row.id}`, { 
            method: 'DELETE' 
          })
          
          if (response.code === 200) {
            this.$message.success('删除成功')
            this.loadEpisodeList()
          } else {
            this.$message.error(response.msg || '删除失败')
          }
        } catch (error) {
          this.$message.error('删除失败: ' + (error.message || '未知错误'))
        }
      }).catch(() => {})
    },

    // 提交表单
    handleSubmit() {
      this.$refs.episodeForm.validate(async (valid) => {
        if (valid) {
          // 验证是否已填写视频URL（无论是上传还是输入）
          if (!this.episodeForm.videoUrl || !this.episodeForm.videoUrl.trim()) {
            this.$message.error(this.episodeForm.videoSourceType === 'upload' ? '请先上传视频文件' : '请输入视频URL地址')
            return
          }
          
          this.submitLoading = true
          try {
            // 构建提交数据,移除videoFile对象
            const submitData = {
              dramaId: this.episodeForm.dramaId,
              episodeNumber: this.episodeForm.episodeNumber,
              title: this.episodeForm.title.trim(),
              duration: this.episodeForm.duration,
              videoUrl: this.episodeForm.videoUrl.trim(),
              status: this.episodeForm.status
            }
            
            // 编辑时需要ID
            if (this.episodeForm.id) {
              submitData.id = this.episodeForm.id
            }
            
            console.log('提交数据:', submitData)
            
            let response
            if (this.episodeForm.id) {
              // 编辑单个集数
              response = await request('/admin/drama/episode/update', { 
                method: 'PUT',
                body: JSON.stringify(submitData)
              })
            } else {
              // 新增单个集数(使用批量添加接口,传入数组)
              response = await request('/admin/drama/episodes/batch-add', { 
                method: 'POST',
                body: JSON.stringify([submitData])
              })
            }
            
            if (response.code === 200) {
              this.$message.success(this.episodeForm.id ? '更新成功' : '新增成功')
              this.dialogVisible = false
              this.loadEpisodeList()
            } else {
              this.$message.error(response.msg || '操作失败')
            }
          } catch (error) {
            this.$message.error('操作失败: ' + (error.message || '未知错误'))
          } finally {
            this.submitLoading = false
          }
        }
      })
    },

    // 关闭对话框
    handleDialogClose() {
      this.$refs.episodeForm.resetFields()
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.loadEpisodeList()
    },

    // 页码改变
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadEpisodeList()
    }
  }
}
</script>

<style scoped>
.drama-episode-management {
  padding: 20px;
}

.box-card {
  border-radius: 8px;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}

.search-bar {
  display: flex;
  align-items: center;
}
</style>
